import { Component, Input, OnInit } from "@angular/core";
import { Company, User } from "@core/models";
import { CompanyService, UserService } from "@core/services";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Observable, forkJoin } from "rxjs";

@Component({
  selector: "app-booked-delivery-note",
  templateUrl: "./booked-delivery-note.component.html",
  styleUrls: ["./booked-delivery-note.component.scss"],
})
export class BookedDeliveryNoteComponent implements OnInit {
  @Input() booked: any[] = [];
  @Input() fileName: string;
  @Input() type: string;
  @Input() transaction: any;
  blobUrl: any;
  company: Company;
  users: User[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    public companyService: CompanyService,
    public userService: UserService
  ) {}

  ngOnInit() {
    this.getBookedType();
  
    if (this.booked.length > 0) {
      const firstBooking = this.booked[0];
  
      this.companyService
        .getCompany(firstBooking.companyUuid)
        .subscribe((company: Company) => {
          this.company = company;
        });
  
      const userUuids = this.booked.reduce((acc, b) => {
        const uuids = Array.isArray(b.userUuid) ? b.userUuid : [b.userUuid];
        return acc.concat(uuids);
      }, []).filter((uuid, i, arr) => uuid && arr.indexOf(uuid) === i);
      
      if (userUuids.length > 0) {
        const userRequests: Observable<User>[] = userUuids.map(uuid =>
          this.userService.getUserByUuid(uuid)
        );
  
        forkJoin(userRequests).subscribe((users: User[]) => {
          this.users = users;
        });
      }
    }
  }

  downloadPDFBooked() {
      const DATA: any = document.getElementById(`delivery-note`);
      if (!DATA) return;
  
      const hiddenContainer = document.createElement('div');
      hiddenContainer.style.width = '570px';
      hiddenContainer.style.height = 'auto';
      hiddenContainer.style.position = 'absolute';
      hiddenContainer.style.top = '-9999px';
      hiddenContainer.appendChild(DATA.cloneNode(true));
  
      document.body.appendChild(hiddenContainer);
  
      const h = hiddenContainer.scrollHeight;
      const w = hiddenContainer.offsetWidth;
  
      html2canvas(hiddenContainer, { scale: 2 }).then((canvas) => {
        const FILEURI = canvas.toDataURL('image/png');
        let PDF = new jsPDF('p', 'px', 'a4');
        let fileWidth = PDF.internal.pageSize.getWidth();
        let fileHeight = PDF.internal.pageSize.getHeight();
        let aspectRatio = w / h;
  
        let imgWidth, imgHeight;
        imgHeight = h > 500 ? fileHeight * 0.6 : h > 420 && h < 480 ? fileHeight * 0.45 : fileHeight * 0.5;
        imgWidth = imgHeight * aspectRatio;
  
        const x = (fileWidth - imgWidth) / 2;
  
        PDF.addImage(FILEURI, 'PNG', x, 20, imgWidth, imgHeight);
        PDF.save(`${ this.booked[0].deliveryNote ?? this.booked[0].uuid}-Vyootrip.pdf`);
  
        document.body.removeChild(hiddenContainer);
      });
  }

  closeModal() {
    this.activeModal.dismiss("Cross click");
  }

  getBookedType() {
    switch (this.type?.toLowerCase()) {
      case "bookingApiReference":
      case "custom-booking":
      case "hotel":
      case "room":
        this.type = 'common.hotel'
        break;
      case "flight":
        this.type = 'common.flight'
        break;
      case "train":
        this.type = 'common.train'
        break;
      case "car":
        this.type = 'common.car'
        break;
      case "expense":
      default:
        this.type = 'common.other'
        break;
    }
  }

  getUnitPrice(bookedItem: any): number {
    if (this.transaction?.type === 'refund') {
      const count = this.booked?.length || 1;

      if (this.transaction?.service === 'flight' || this.transaction?.service === 'train') {
        const breakdown = this.transaction?.refundBreakdown?.find(
          (r: any) => r.uuid === bookedItem.uuid
        );
        if (breakdown) return breakdown.amount;

      if (count === 1) return this.transaction.amount;

      return 0;
      }

      return Number((this.transaction.amount / count).toFixed(2));
    }

    if ((this.type === 'flight' || this.type === 'common.flight') && bookedItem?.price?.individualPrice !== undefined) {
      return bookedItem.price.individualPrice;
    }

    const rawPrice = bookedItem?.price;
      return typeof rawPrice === 'number'
        ? rawPrice
        : typeof rawPrice?.totalPrice === 'number'
        ? rawPrice.totalPrice
        : 0;
  }

  getTotalPrice(): number {
    if (!Array.isArray(this.booked)) return 0;

    if (this.transaction?.type === 'refund' && this.transaction?.service === 'flight') {
      return this.transaction.amount || 0;
    }

    return this.booked
      .map(b => this.getUnitPrice(b))
      .reduce((acc, price) => acc + price, 0);
  }

  getDeliveryNotes(): string {
    if (!Array.isArray(this.booked)) return '-';
  
    return this.booked
      .map(b => b.deliveryNote)
      .filter(Boolean)
      .join(', ');
  }

  getUserForBookedItem(bookedItem: any): User[] {
    if (!Array.isArray(this.booked)) return [];
  
    if (this.booked.length === 1) {
      return this.users;
    }
  
    if (!bookedItem?.userUuid) return [];
  
    const uuids = Array.isArray(bookedItem.userUuid)
      ? bookedItem.userUuid
      : [bookedItem.userUuid];
  
    return this.users.filter(user => uuids.includes(user.uuid));
  }
  
}
